import { doFrameLoop, FrameCancelFunction } from "../doFrameLoop";
import { Bounds } from "./Bounds";
import { BoundsObserverEntry } from "./BoundsObserverEntry";
import { BoundsChangeCallback } from "./BoundsChangeCallback";

/**
 * Provides a method for observing when an element's visual bounds change.
 */
export class BoundsObserver {
  private static _observers: Array<BoundsObserver> = [];
  private static _frameLoopCancel: FrameCancelFunction | null = null;
  private static _skip = 0;

  /**
   * Runs the animation frame loop to update observers.
   */
  private static run(): void {
    BoundsObserver._frameLoopCancel = doFrameLoop(
      () => {
        if (BoundsObserver._skip === -1) {
          return; // Just skip processing until the next frame.
        }

        const results: Array<{
          readonly observer: BoundsObserver;
          readonly entries: ReadonlyArray<BoundsObserverEntry>;
        }> = [];

        // calculate bounds of observed entries.
        for (const observer of this._observers) {
          results.push({ observer, entries: observer.collect() });
        }

        // now update based on bounds changes.
        for (const result of results) {
          result.observer.update(result.entries);
        }
      },
      BoundsObserver._skip === -1 ? 0 : BoundsObserver._skip,
    );
  }

  /**
   * Adds an observer to the list of observers to process.
   * @param observer The observer to add.
   */
  private static addObserver(observer: BoundsObserver): void {
    if (BoundsObserver._observers.includes(observer)) return;

    BoundsObserver._observers.push(observer);
    if (BoundsObserver._observers.length === 1) BoundsObserver.run();
  }

  /**
   * Removes an observer from the list of observers to process.
   * @param observer The observer to remove.
   */
  private static removeObserver(observer: BoundsObserver): void {
    BoundsObserver._observers = BoundsObserver._observers.filter((entry) => entry !== observer);
    if (BoundsObserver._observers.length === 0 && BoundsObserver._frameLoopCancel) {
      BoundsObserver._frameLoopCancel();
      BoundsObserver._frameLoopCancel = null;
    }
  }

  /**
   * Sets the priorty of bounds calculations.
   * @param priority The priority at which bounds changes should be calculated.
   */
  static setPriority(priority: "skip" | "low" | "normal"): void {
    switch (priority) {
      case "skip":
        BoundsObserver._skip = -1;
        break;
      case "low":
        BoundsObserver._skip = 2;
        break;
      case "normal":
        BoundsObserver._skip = 0;
        break;
      default:
        break;
    }

    if (BoundsObserver._observers.length > 0) {
      // Reset the run loop.
      if (BoundsObserver._frameLoopCancel) {
        BoundsObserver._frameLoopCancel();
        BoundsObserver._frameLoopCancel = null;
        BoundsObserver.run();
      }
    }
  }

  private _entries: Array<{
    target: HTMLElement;
    previousBounds: Bounds | null;
  }>;

  private _callback: BoundsChangeCallback;
  private _paused: boolean;

  /**
   * Collects the bounds that have changed.
   * @returns The updated bounds.
   */
  private collect(): ReadonlyArray<BoundsObserverEntry> {
    const entries: Array<BoundsObserverEntry> = [];

    for (const entry of this._entries) {
      const { target, previousBounds } = entry;
      const { top, left, width, height } = target.getBoundingClientRect();
      const { offsetTop, offsetLeft } = target;

      if (previousBounds) {
        const { top: previousTop, left: previousLeft, width: previousWidth, height: previousHeight } = previousBounds;
        if (top === previousTop && left === previousLeft && width === previousWidth && height === previousHeight) continue;
      }

      const bounds: Bounds = { top, left, width, height };
      entries.push({ target, bounds, previousBounds, offsetTop, offsetLeft });
      entry.previousBounds = bounds;
    }

    return entries;
  }

  /**
   * Updates the bounds observer.
   * @param entries The entries to update.
   */
  private update(entries: ReadonlyArray<BoundsObserverEntry>): void {
    if (entries.length === 0) return;
    this._callback(entries, this);
  }

  /**
   * Begins observing the element for changes to its visual bounding box.
   * @param target The element to observe.
   */
  observe(target: HTMLElement): void {
    if (this._entries.find((entry) => entry.target === target)) return;
    this._entries.push({ target, previousBounds: null });
    if (this._paused) return;

    this.resume();
  }

  /**
   * Stops observing the element.
   * @param target The element to stop observing.
   */
  unobserve(target: HTMLElement): void {
    this._entries = this._entries.filter((entry) => entry.target !== target);
    if (this._entries.length === 0) {
      BoundsObserver.removeObserver(this);
    }
  }

  /**
   * Clears all registered observers.
   */
  disconnect(): void {
    BoundsObserver.removeObserver(this);
    this._entries = [];
  }

  /**
   * Pauses observing for changes.
   */
  pause(): void {
    BoundsObserver.removeObserver(this);
  }

  /**
   * Resumes observing for changes.
   */
  resume(): void {
    BoundsObserver.addObserver(this);
  }

  /**
   * Initializes a new instance of the BoundsObserver class.
   * @param callback The function to call when the element bounds change.
   */
  constructor(callback: BoundsChangeCallback) {
    this._entries = [];
    this._paused = false;
    this._callback = callback;
  }
}
